import { compact } from 'lodash'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Booking } from '../../models/Booking'
import { Estimate } from '../../models/Estimate'
import { Gift } from '../../models/Gift'
import { Invoice } from '../../models/Invoice'
import { Package } from '../../models/Package'
import { Payment } from '../../models/Payment'
import { Subscription } from '../../models/Subscription'
import { User } from '../../models/User'
import { currencyFormat } from '../../utils/numbers'
import { Icon } from '../Icon'
import Button from '../app/design/Button'
import Card from '../app/design/Card'
import Divider from '../app/design/Divider'
import Heading from '../app/design/Heading'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import { RecordStatusChecklist } from '../common/RecordStatusChecklist'
import { BaseRecordView } from '../common/sections'
import { ContactAvatar } from '../contacts/ContactAvatar'
import { WorkflowPage } from '../workflows/WorkflowPage'

type Props = {
    payment: Payment
    contact: User
    sourceTransaction:
        | { type: 'booking'; booking: Booking }
        | { type: 'estimate'; estimate: Estimate }
        | { type: 'package'; aPackage: Package }
        | { type: 'gift'; gift: Gift }
        | { type: 'subscription'; subscription: Subscription }
        | { type: 'invoice'; invoice: Invoice }
        | null
    forVendor: boolean
    isSignedInUserPayer: boolean
    owner: User
}

export const PaymentView = ({
    payment,
    contact,
    sourceTransaction,
    forVendor,
    owner,
    isSignedInUserPayer,
}: Props) => {
    const sourceTransactionData = ((): {
        title: string
        subtitle: string
        icon: ReactNode
        href: string
        amount: number
    } | null => {
        if (sourceTransaction === null) {
            return null
        }

        switch (sourceTransaction.type) {
            case 'booking':
                return {
                    title: sourceTransaction.booking.title,
                    subtitle: sourceTransaction.booking.client?.name ?? '',
                    icon:
                        sourceTransaction.booking.item?.iconConfig?.type === 'image' ? (
                            <img
                                src={sourceTransaction.booking.item.iconConfig.uri}
                                className="size-12 rounded-full"
                            />
                        ) : sourceTransaction.booking.item?.iconConfig?.type === 'icon' ? (
                            <Icon
                                icon={sourceTransaction.booking.item.iconConfig.icon}
                                background="round"
                            />
                        ) : (
                            <Icon icon="package.large" background="round" />
                        ),
                    href: `/bookings/${sourceTransaction.booking.id}`,
                    amount: sourceTransaction.booking.amount ?? 0,
                }
            case 'estimate':
                return {
                    title: `Estimate #${sourceTransaction.estimate.number !== undefined ? sourceTransaction.estimate.number : ' (to be generated)'}`,
                    subtitle: sourceTransaction.estimate.client?.name ?? '',
                    icon: <Icon icon="package.large" background="round" />,
                    href: `/estimates/${sourceTransaction.estimate.id}`,
                    amount: sourceTransaction.estimate.amount ?? 0,
                }
            case 'package':
                return {
                    title: sourceTransaction.aPackage.name ?? '',
                    subtitle: sourceTransaction.aPackage.client?.name ?? '',
                    icon:
                        sourceTransaction.aPackage.item?.iconConfig?.type === 'image' ? (
                            <img
                                src={sourceTransaction.aPackage.item.iconConfig.uri}
                                className="size-12 rounded-full"
                            />
                        ) : sourceTransaction.aPackage.item?.iconConfig?.type === 'icon' ? (
                            <Icon
                                icon={sourceTransaction.aPackage.item.iconConfig.icon}
                                background="round"
                            />
                        ) : (
                            <Icon icon="package.large" background="round" />
                        ),
                    href: `/packages/${sourceTransaction.aPackage.id}`,
                    amount: sourceTransaction.aPackage.chargeAmount ?? 0,
                }
            case 'gift':
                return {
                    title: sourceTransaction.gift.name ?? '',
                    subtitle: sourceTransaction.gift.client?.name ?? '',
                    icon:
                        sourceTransaction.gift.item?.iconConfig?.type === 'image' ? (
                            <img
                                src={sourceTransaction.gift.item.iconConfig.uri}
                                className="size-12 rounded-full"
                            />
                        ) : sourceTransaction.gift.item?.iconConfig?.type === 'icon' ? (
                            <Icon
                                icon={sourceTransaction.gift.item.iconConfig.icon}
                                background="round"
                            />
                        ) : (
                            <Icon icon="gift.large" background="round" />
                        ),
                    href: `/gifts/${sourceTransaction.gift.id}`,
                    amount: sourceTransaction.gift.item?.amount ?? 0,
                }
            case 'subscription':
                return {
                    title: sourceTransaction.subscription.plan?.name ?? '',
                    subtitle: sourceTransaction.subscription.client?.name ?? '',
                    icon:
                        sourceTransaction.subscription.plan?.iconConfig?.type === 'image' ? (
                            <img
                                src={sourceTransaction.subscription.plan.iconConfig.uri}
                                className="size-12 rounded-full"
                            />
                        ) : sourceTransaction.subscription.plan?.iconConfig?.type === 'icon' ? (
                            <Icon
                                icon={sourceTransaction.subscription.plan.iconConfig.icon}
                                background="round"
                            />
                        ) : (
                            <Icon icon="package.large" background="round" />
                        ),
                    href: `/subscriptions/${sourceTransaction.subscription.id}`,
                    amount: sourceTransaction.subscription.chargeAmount ?? 0,
                }
            case 'invoice':
                return {
                    title: `Invoice #${sourceTransaction.invoice.number !== undefined ? sourceTransaction.invoice.number : ' (to be generated)'}`,
                    subtitle: sourceTransaction.invoice.client?.name ?? '',
                    icon: <Icon icon="document2.large" background="round" />,
                    href: `/invoices/${sourceTransaction.invoice.id}`,
                    amount: sourceTransaction.invoice.amount ?? 0,
                }
        }
    })()

    return (
        <BaseRecordView.Container>
            <BaseRecordView.LeftColumn>
                <Card size="sm">
                    <div className="flex flex-col items-center justify-center gap-y-1">
                        <Heading size="lg" className="text-center">
                            {currencyFormat(payment.serverAmount ?? payment.amount ?? 0)}
                        </Heading>

                        {payment.source !== undefined ? (
                            payment.source.displayTitleWithEnding !== undefined && (
                                <Paragraph
                                    size="lg"
                                    className="flex items-center justify-center gap-x-2 font-normal text-gray-600"
                                >
                                    {payment.source.icon} {payment.source.displayTitleWithEnding}
                                </Paragraph>
                            )
                        ) : payment.sourceDisplay !== undefined ? (
                            <Paragraph
                                size="lg"
                                className="flex items-center justify-center gap-x-2 font-normal text-gray-600"
                            >
                                {payment.sourceDisplay}
                            </Paragraph>
                        ) : null}

                        <Paragraph size="lg" className="font-normal text-gray-600">
                            {payment.refnum}
                        </Paragraph>
                    </div>

                    <Divider className="my-6" />

                    <div className="flex flex-col items-stretch justify-start gap-y-6">
                        {!!payment.subaccount && (
                            <BaseRecordView.IconField
                                icon={
                                    <ContactAvatar
                                        user={payment.subaccount}
                                        containerClassName="row-span-2"
                                        hasHoverEffect
                                    />
                                }
                                title={payment.subaccount.name ?? ''}
                                subtitle={payment.subaccount.labelString}
                                href={`/subaccounts/${payment.subaccount.id}`}
                            />
                        )}

                        {contact.id && (
                            <BaseRecordView.IconField
                                icon={<ContactAvatar user={contact} hasHoverEffect />}
                                title={contact.name ?? ''}
                                subtitle={forVendor ? 'Client' : 'Payee'}
                                secondaryView={
                                    <a href={`/messages/${contact.id}`}>
                                        <Button intent="secondary" size="small">
                                            Chat
                                        </Button>
                                    </a>
                                }
                                href={`/clients/${contact.id}`}
                            />
                        )}
                    </div>
                </Card>

                <Card size="sm" title="Status">
                    <RecordStatusChecklist
                        items={payment.statuses.map((status) => ({
                            title: status.title,
                            subtitle: status.date?.format('MMM Do, YYYY [at] h:mm A'),
                            status: status.date !== undefined ? 'checked' : 'empty',
                        }))}
                        className="mt-6"
                    />
                </Card>
            </BaseRecordView.LeftColumn>

            <BaseRecordView.RightColumn>
                <Card title="Payment details">
                    <div className="mt-8">
                        <WorkflowPage.ReceiptView
                            receipt={{
                                modifiers: {
                                    extraRows: compact([
                                        {
                                            title: 'Total',
                                            icon: 'money2.large',
                                            amount: payment.total ?? 0,
                                        },
                                        (payment.tip ?? 0) > 0
                                            ? {
                                                  title: 'Gratuity',
                                                  icon: 'money.large',
                                                  amount: payment.tip ?? 0,
                                              }
                                            : null,
                                        (payment.discount ?? 0) > 0
                                            ? {
                                                  title: 'Discount',
                                                  icon: 'discount.large',
                                                  amount: -1 * Math.abs(payment.discount ?? 0),
                                              }
                                            : null,
                                        (payment.taxAmount ?? 0) > 0
                                            ? {
                                                  title: 'Sales tax',
                                                  icon: 'percentage2.large',
                                                  amount: payment.taxAmount ?? 0,
                                              }
                                            : null,
                                        {
                                            title:
                                                (payment.surcharge ?? 0)
                                                    ? 'Payment (3% surcharge)'
                                                    : 'Payment',
                                            icon: 'percentage2.large',
                                            amount: payment.serverAmount ?? payment.amount ?? 0,
                                        },
                                        (payment.refundedAmount ?? 0) > 0 && forVendor
                                            ? {
                                                  title: 'Payout',
                                                  icon: 'dollar.large',
                                                  amount: payment.payout ?? 0,
                                              }
                                            : null,
                                    ]),
                                },
                                total:
                                    (payment.refundedAmount ?? 0) > 0
                                        ? payment.refundedAmount
                                        : forVendor
                                          ? (payment.payout ?? 0)
                                          : undefined,
                                totalText:
                                    (payment.refundedAmount ?? 0) > 0
                                        ? 'Refund'
                                        : forVendor
                                          ? 'Payout'
                                          : undefined,
                            }}
                            transaction={payment}
                            owner={owner}
                            client={contact}
                        />

                        {(payment.refundedAmount ?? 0) > 0 &&
                            (payment.refundReason !== undefined || !!payment.refundDetails) && (
                                <>
                                    <Divider />

                                    <div className="flex items-start justify-start gap-x-4">
                                        <Icon icon="info.large" />
                                        <div className="flex flex-col items-stretch justify-start gap-y-2">
                                            <Label size="md" className="text-gray-700">
                                                Reason for refund
                                            </Label>

                                            <Paragraph size="md" className="text-gray-600">
                                                {
                                                    {
                                                        cancel: 'Services not rendered',
                                                        overcharge: 'Overcharged',
                                                        courtesy: 'Courtesy refund',
                                                        paid: 'Client already paid',
                                                    }[payment.refundReason ?? '']
                                                }
                                            </Paragraph>

                                            <Paragraph size="md" className="text-gray-600">
                                                {payment.refundDetails}
                                            </Paragraph>
                                        </div>
                                    </div>
                                </>
                            )}
                    </div>
                </Card>

                {sourceTransactionData !== null && (
                    <Card title="Source transaction">
                        <Link
                            to={sourceTransactionData.href}
                            className="group relative mt-8 flex items-center justify-center gap-x-4"
                        >
                            <div className="absolute -inset-4 z-10 rounded-lg bg-black/4 opacity-0 group-hover:opacity-100" />

                            {sourceTransactionData.icon}
                            <div className="z-20 flex grow flex-col items-stretch justify-start gap-y-1">
                                <div className="flex items-center justify-between">
                                    <Label size="md" className="text-gray-700">
                                        {sourceTransactionData.title}
                                    </Label>
                                    <Label size="md" className="text-gray-700">
                                        {currencyFormat(sourceTransactionData.amount, true)}
                                    </Label>
                                </div>
                                <Paragraph size="sm" className="text-gray-600">
                                    {sourceTransactionData.subtitle}
                                </Paragraph>
                            </div>
                        </Link>
                    </Card>
                )}

                {payment.memo && (
                    <Card title="Memo">
                        <Paragraph className="mt-8 whitespace-pre-wrap text-gray-600">
                            {payment.memo}
                        </Paragraph>
                    </Card>
                )}
            </BaseRecordView.RightColumn>
        </BaseRecordView.Container>
    )
}
